import Money from 'common/Money';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AmountDisplay} from 'src/AmountDisplay';
import {formatListDate} from 'src/lib/displayDate';
import type {FullRawTransactionFragment} from '@transactions/graphql';

type Consumer = FullRawTransactionFragment['consumers'][number];

type Props = {
  consumers: Consumer[];
  currency: string;
  totalAmount: number;
  remainingAmount: number | null | undefined;
};

const ConsumersList = ({
  consumers,
  currency,
  totalAmount,
  remainingAmount,
}: Props) => {
  const consumedAmount = consumers.reduce((sum, c) => sum + c.amount, 0);
  const remaining = remainingAmount ?? totalAmount - consumedAmount;

  if (consumers.length === 0 && remaining === totalAmount) {
    return (
      <div className="text-muted-foreground px-4 py-3">
        Not yet allocated to any transaction
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {consumers.map((consumer, index) => {
        const tx = consumer.transaction;
        const consumedMoney = Money.fromInt(consumer.amount, currency);
        const isPartial = consumer.amount !== totalAmount;

        // Get display info based on transaction type
        // Payment = netAmount !== 0, Transfer = netAmount === 0
        const isPayment = tx.netAmount !== 0;
        const label = isPayment ? (tx.payee ?? tx.description) : tx.description;
        const date = tx.date;
        const path = `/transactions/${tx._id}`;

        return (
          <Link
            key={`${consumer.transactionId}-${index}`}
            to={path}
            className="not-last:border-border hover:bg-muted/50 grid w-full cursor-pointer [grid-template-columns:1fr_auto] items-center gap-x-5 gap-y-1.5 px-4 py-3 transition-colors [grid-template-areas:'left_right''left_right'] not-last:border-b"
          >
            <div className="flex min-w-0 flex-col gap-1.5 [grid-area:left]">
              <div className="text-foreground truncate" title={label ?? ''}>
                {label}
              </div>
              <div className="text-muted-foreground flex flex-wrap items-center gap-1.5">
                <span className="text-xs capitalize">
                  {isPayment ? 'Payment' : 'Transfer'}
                </span>
                <span className="text-xs">{formatListDate(date)}</span>
              </div>
            </div>
            <div className="flex min-w-0 flex-col items-end justify-center gap-1 pl-3 text-right [grid-area:right]">
              <AmountDisplay amount={consumedMoney} />
              {isPartial && (
                <div className="text-muted-foreground text-xs">
                  of{' '}
                  <AmountDisplay
                    amount={Money.fromInt(totalAmount, currency)}
                    variant="plain"
                    inline
                  />
                </div>
              )}
            </div>
          </Link>
        );
      })}
      {remaining !== 0 && (
        <div className="not-last:border-border flex items-center justify-between gap-4 px-4 py-3 not-last:border-b">
          <div className="text-muted-foreground">Remaining</div>
          <AmountDisplay amount={Money.fromInt(remaining, currency)} />
        </div>
      )}
      <div className="border-border bg-muted/50 flex items-center justify-between gap-4 border-t px-4 py-2">
        <div className="font-medium">Total</div>
        <AmountDisplay amount={Money.fromInt(totalAmount, currency)} />
      </div>
    </div>
  );
};

export {ConsumersList};
